import { useState } from "react"

import { 
  ColumnSelector, 
  CustomModal, 
  EmptyPlaceholder, 
  FilterSelector, 
  LoadingPlaceholder, 
  SearchInput 
} from "@components"

import { GET_LESSON_SELECTOR } from "@graphql"
import { stringToColor, staffFullname } from "@helpers"
import { useColumnManager } from "@hooks"
import { useQuery } from "@apollo/client"

import { 
  Button, 
  Checkbox, 
  Chip, 
  ModalBody, 
  ModalContent, 
  ModalFooter, 
  ModalHeader, 
  Pagination, 
  Table, 
  TableColumn, 
  TableBody, 
  TableHeader, 
  TableRow, 
  TableCell 
} from "@heroui/react"

import { CircleStackIcon } from "@heroicons/react/24/outline"

import { 
  columns, 
  defaultColumns, 
  filters 
} from "./data"

import PropTypes from "prop-types"

const LessonSelectorModal = ({ 
  clientId, 
  handleClose, 
  handleSubmit, 
  isSubmitting, 
  selectedLessonsByUser, 
  show = false 
}) => {
  const [disabledLessonIds, setDisabledLessonIds] = useState([])
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState(["all"])
  const [selectedLessons, setSelectedLessons] = useState([])
  const [sortDescriptor, setSortDescriptor] = useState({ column: "name", direction: "ascending" })
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)
  const [paginatorInfo, setPaginatorInfo] = useState(null)

  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "lessons.selector", defaultColumns)


  const { data, loading } = useQuery(GET_LESSON_SELECTOR, {
    variables: { 
      page,
      perPage,
      clientId: clientId,
      
      orderBy: [
        {
          column: sortDescriptor.column.toUpperCase(),
          order: sortDescriptor.direction === "ascending" ? "ASC" : "DESC",
        },
      ],

      where: selectedFilters.includes("sync") 
        ? { column: "IS_SYNC", value: true } 
        : selectedFilters.includes("custom") 
        ? { column: "IS_SYNC", value: false } 
        : null,

      filterByIds: selectedFilters.includes("selected") ? [...selectedLessons.map((lesson => lesson.id)), -1] : [],
	    searchTerm
    },

    fetchPolicy: "network-first",

    onCompleted: data => {
      const lessonUserIds = selectedLessonsByUser.map(lessonUser => `${lessonUser.lesson_id}-${lessonUser.user_id}`)

      const lessonIds = data.client.lessons.data
        .filter(lesson => lessonUserIds.includes(`${lesson.id}-${lesson?.teacher?.user_id}`))
        .map(lesson => lesson.id)

      setPaginatorInfo(data.client.lessons.paginatorInfo)
      setDisabledLessonIds(lessonIds)
    }
  })

  const getSelectableLessons = (lessons) => {
    const selectedIds = new Set(selectedLessons.map(lesson => lesson.id))
    const disabledIds = new Set(disabledLessonIds)

    return lessons.filter(
      lesson => !selectedIds.has(lesson.id) && !disabledIds.has(lesson.id)
    )
  }
  
  const getSubjectStyles = subject => {
    if (subject === null) return { backgroundColor: "#c0bcc2", color: "white" }

    return {
      backgroundColor: stringToColor(subject, "subject"),
      color: "white",
    }
  }
  
  const handleRenderCell = (lesson, column, isDisabled, isSelected) => {
    if (column === "select") return <Checkbox color="secondary" isSelected={isSelected} isDisabled={isDisabled} onChange={() => handleRowSelection(lesson.id)} />

    if (column === "class") return lesson.name || "N/A"
    
    if (column === "teacher") {
      return staffFullname(lesson?.teacher) || "N/A"
    }

    if (column === "subject") {
      return (
        <Chip size="md" radius="sm" style={getSubjectStyles(lesson?.subject?.name)}>
          {lesson?.subject?.name || "N/A"}
        </Chip>
      )
    }

    if (column === "type" && lesson.is_sync) {
      return <Chip classNames={{ base: "bg-sb-sync", content: "text-white" }} size="md" variant="flat">Sync</Chip>
    }

    if (column === "type" && !lesson.is_sync) {
      return <Chip classNames={{ base: "bg-sb-sync-custom", content: "text-white" }} size="md" variant="flat">Custom</Chip>
    }

    if (column === "students") {
      return lesson.studentCount || 0
    }
  }

  const handleRowSelection = id => {
    const hasLesson = selectedLessons.filter(lesson => lesson.id === id).length > 0

    hasLesson 
      ? setSelectedLessons(previousLessons => [...previousLessons].filter(previousLesson => previousLesson.id !== id))
      : setSelectedLessons(previousLessons => [...previousLessons, data.client.lessons.data.find(lesson => lesson.id === id)])
  }
  
  const handleSearchChange = searchTerm => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  const handleSearchClear = () => {
    setSearchTerm("")
    setPage(1)
  }

  const handleSelectCurrentPage = (selectableLessons) => setSelectedLessons(previousLessons => [...previousLessons, ...selectableLessons])
  
  const handleSortChange = sortDescriptor => {
    setSortDescriptor(sortDescriptor)
    setPage(1)
  }

  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleToggleFilter = selectedFilters => {
    setSelectedFilters([...selectedFilters])
    setPage(1)
  }
  
  const addLessonsCaption = selectedLessons.length > 0 ? `Add ${selectedLessons.length} Lessons` : "Add Lessons"
  const lessons = data?.client?.lessons?.data || []
  const selectableLessons = getSelectableLessons(lessons)
  const selectAllCaption = `Select page (${selectableLessons.length})`
    
  return (
    <CustomModal 
      isOpen={show}
      classNames={{ 
        base: "h-[700px]",
        body: "gap-0",
        header: "bg-indigo-800 rounded-t-xl text-white",
        wrapper: "md:items-center",
        closeButton: "hover:bg-white/5 text-white mt-2 mr-2",
      }}         
      size="5xl"
      onClose={() => handleClose()}
      isDismissable={false}
      isKeyboardDismissDisabled={false}
      scrollBehavior="inside"  
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">MIS Lesson Selector</ModalHeader>

            <ModalBody>
              <div className="flex items-center gap-2 mt-2 mb-3">
                <SearchInput
                  debounceMilliSeconds={300}
                  placeholder="Search by name / teacher / subject"
                  handleChange={(searchTerm) => handleSearchChange(searchTerm)}
                  handleClear={handleSearchClear}
                  value={searchTerm}
                />

                <ColumnSelector
                  columns={columns}
                  selectedColumns={selectedColumns}
                  toggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
                />

                <FilterSelector
                  filters={filters}
                  selectionMode="single"
                  closeOnSelect={true}
                  selectedFilters={selectedFilters}
                  toggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
                />

                <Button 
                  color="success" 
                  isDisabled={loading || selectableLessons.length === 0} 
                  onPress={() => handleSelectCurrentPage(selectableLessons)} 
                  className="flex-shrink-0 text-white"
                >
                  {selectAllCaption}
                </Button>
              </div>

              <Table 
                removeWrapper 
                disabledKeys={disabledLessonIds} 
                isStriped
                classNames={{ wrapper: "min-h-full" }}
                sortDescriptor={sortDescriptor}
                onSortChange={sortDescriptor => handleSortChange(sortDescriptor)}
              >
                <TableHeader>
                  {selectedColumns.map(columnKey => {
                    const column = columns[columnKey]
                    return <TableColumn key={column.id} allowsSorting={column.sortable}>{column.name}</TableColumn>
                  })}
                </TableHeader>

                <TableBody>
                  {lessons?.length > 0 && !loading &&lessons.map(lesson => {
                    const isDisabled = disabledLessonIds.includes(lesson.id)
                    const isSelected = selectedLessons.some((selectedLesson) => selectedLesson.id === lesson.id) || disabledLessonIds.includes(lesson.id)
                    
                    return (
                      <TableRow key={lesson.id}>
                        {selectedColumns.map(columnKey => (
                          <TableCell key={columnKey}>
                            {handleRenderCell(lesson, columnKey, isDisabled, isSelected)}
                          </TableCell>
                        ))}
                      </TableRow>                        
                    )
                  })}
                </TableBody>
              </Table>
              
              {loading && <LoadingPlaceholder classNames="mt-2" caption="Please wait. Loading data..." icon={CircleStackIcon} />}
              {!loading && lessons?.length === 0 && <EmptyPlaceholder classNames="mt-2" title="No Lessons Found" description="No lessons found! Try clearing any filters." />}
            </ModalBody>
            
            <ModalFooter>
              <div className="flex items-center justify-between w-full">
                {paginatorInfo?.lastPage > 1 && (
                  <Pagination
                    isCompact
                    showControls
                    showShadow
                    color="secondary"
                    page={page}
                    total={paginatorInfo?.lastPage}
                    onChange={page => setPage(page)}
                  />
                )}

                <Button className="ml-auto"	color="secondary" onPress={() => handleSubmit(selectedLessons)} isDisabled={selectedLessons.length === 0} isLoading={isSubmitting}>
                  {addLessonsCaption}
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </CustomModal>
  )
}

LessonSelectorModal.propTypes = {
  clientId: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  selectedLessonsByUser: PropTypes.array,
  show: PropTypes.bool,
}

export default LessonSelectorModal
